import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../lib/api";
import { useLang } from "../contexts/LangContext";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { t, lang, toggle } = useLang();

  const links = [
    { to: "/", labelKey: "nav.home" },
    { to: "/occasions", labelKey: "nav.occasions" },
    { to: "/diensten", labelKey: "nav.diensten" },
    { to: "/over-ons", labelKey: "nav.over" },
    { to: "/reviews", labelKey: "nav.reviews" },
    { to: "/nieuws", labelKey: "nav.nieuws" },
    { to: "/contact", labelKey: "nav.contact" },
  ];

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between h-28 sm:h-32">
          <Link to="/" className="flex items-center gap-3 sm:gap-4 -ml-2 sm:ml-0" data-testid="header-logo-link">
            <Logo className="h-24 sm:h-28 w-auto" priority />
            <span className="hidden md:flex flex-col leading-tight">
              <span className="font-display font-black text-2xl tracking-tight">MP AUTOBEDRIJF</span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Arnhem Noord</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.labelKey}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    isActive ? "text-[#e60000]" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {t(l.labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggle}
              className="flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-wider text-white transition-colors"
              data-testid="lang-toggle"
              aria-label="Switch language"
            >
              <Globe size={14} />
              <span className="font-bold">{lang === "nl" ? "EN" : "NL"}</span>
            </button>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="hidden xl:inline-flex items-center gap-2 text-sm text-white/80 hover:text-white whitespace-nowrap"
              data-testid="header-phone"
            >
              <Phone size={16} />
              <span className="whitespace-nowrap">{COMPANY.phone}</span>
            </a>
            <Link to="/afspraak" className="btn-primary text-xs px-5 py-3" data-testid="header-cta-afspraak">
              {t("nav.afspraak")}
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={toggle}
              className="px-2.5 py-1.5 border border-white/15 text-xs uppercase tracking-wider text-white"
              data-testid="lang-toggle-mobile"
              aria-label="Switch language"
            >
              {lang === "nl" ? "EN" : "NL"}
            </button>
            <button
              className="p-2 text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="header-mobile-toggle"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.labelKey}`}
                className={({ isActive }) =>
                  `py-3 border-b border-white/5 text-sm font-semibold uppercase tracking-wider ${
                    isActive ? "text-[#e60000]" : "text-white/80"
                  }`
                }
              >
                {t(l.labelKey)}
              </NavLink>
            ))}
            <Link
              to="/afspraak"
              onClick={() => setOpen(false)}
              className="btn-primary mt-4 w-full"
              data-testid="mobile-cta-afspraak"
            >
              {t("nav.afspraak")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
